/**
 * The small set of form primitives the admin portal needs (SPEC §9.2).
 *
 * §3 permits `shadcn/ui`, and it was evaluated here. It was not adopted: its
 * components are written against its own token vocabulary (`bg-background`,
 * `text-muted-foreground`, `border-input`), and this product already has a
 * palette — §9.2's surface / ink / rule — served from the database and
 * declared once in `index.css`. Vendoring a second vocabulary would mean
 * either rewriting every generated component or maintaining two sets of
 * colours that must be kept in agreement, which is exactly the drift §9.2 is
 * arranged to avoid. Every control M4 needs is a native one, and the project
 * already prefers native inputs (M3 chose native date fields over a picker
 * library for the same reason).
 *
 * Restraint per §9.2: a 1px rule, one card elevation, radii at 6px or below,
 * 150ms transitions, and a visible focus ring on everything.
 */

import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react'
import { useEffect, useRef } from 'react'

const BUTTON_BASE =
  'inline-flex items-center justify-center gap-1.5 rounded-[var(--radius-md)] ' +
  'px-2.5 py-1.5 text-[length:var(--text-xs)] font-medium whitespace-nowrap ' +
  'disabled:opacity-50 disabled:cursor-not-allowed'

const BUTTON_VARIANTS = {
  primary: 'bg-[var(--accent)] text-[var(--color-surface-raised)] hover:opacity-90',
  secondary:
    'border border-[var(--color-rule)] bg-[var(--color-surface-raised)] ' +
    'hover:bg-[var(--color-rule)]/30',
  danger:
    'border border-[var(--color-negative)]/50 text-[var(--color-negative)] ' +
    'hover:bg-[var(--color-negative)]/10',
} as const

export function Button({
  variant = 'secondary',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: keyof typeof BUTTON_VARIANTS
}) {
  return (
    <button
      type="button"
      {...props}
      className={`${BUTTON_BASE} ${BUTTON_VARIANTS[variant]} ${className}`}
    />
  )
}

export function Card({
  title,
  description,
  actions,
  children,
}: {
  title: string
  // `| undefined` throughout: `exactOptionalPropertyTypes` is on, so an
  // optional prop and a prop passed as undefined are different types.
  description?: string | undefined
  actions?: ReactNode | undefined
  children: ReactNode
}) {
  return (
    <section className="elevated rounded-[var(--radius-lg)] border border-[var(--color-rule)] bg-[var(--color-surface-raised)]">
      <header className="flex items-start gap-3 border-b border-[var(--color-rule)] px-4 py-3">
        <div className="min-w-0">
          <h2 className="text-[length:var(--text-base)] font-semibold">{title}</h2>
          {description !== undefined && (
            <p className="mt-0.5 text-[length:var(--text-xs)] text-[var(--color-ink-muted)]">
              {description}
            </p>
          )}
        </div>
        {actions !== undefined && <div className="ml-auto flex gap-2">{actions}</div>}
      </header>
      <div className="p-4">{children}</div>
    </section>
  )
}

export function TextField({
  label,
  hint,
  error,
  className = '',
  ...props
}: InputHTMLAttributes<HTMLInputElement> & {
  label: string
  hint?: string | undefined
  error?: string | undefined
}) {
  return (
    <label className="block">
      <span className="block text-[length:var(--text-xs)] font-medium">{label}</span>
      <input
        {...props}
        aria-invalid={error !== undefined || undefined}
        className={`mt-1 w-full rounded-[var(--radius-md)] border px-2 py-1.5 text-[length:var(--text-sm)] ${
          error === undefined ? 'border-[var(--color-rule)]' : 'border-[var(--color-negative)]'
        } bg-[var(--color-surface)] ${className}`}
      />
      {hint !== undefined && error === undefined && (
        <span className="mt-1 block text-[length:var(--text-2xs)] text-[var(--color-ink-muted)]">
          {hint}
        </span>
      )}
      {error !== undefined && (
        <span className="mt-1 block text-[length:var(--text-2xs)] text-[var(--color-negative)]">
          {error}
        </span>
      )}
    </label>
  )
}

export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode
  tone?: 'neutral' | 'warning' | 'danger' | 'positive'
}) {
  const tones = {
    neutral: 'border-[var(--color-rule)] text-[var(--color-ink-muted)]',
    warning: 'border-[#b8860b]/50 text-[#b8860b]',
    danger: 'border-[var(--color-negative)]/50 text-[var(--color-negative)]',
    positive: 'border-[var(--color-positive)]/50 text-[var(--color-positive)]',
  } as const
  return (
    <span
      className={`inline-flex items-center rounded-[var(--radius-sm)] border px-1.5 py-0.5 text-[length:var(--text-2xs)] ${tones[tone]}`}
    >
      {children}
    </span>
  )
}

/** A plain table shell. The report `DataTable` is for positional report rows. */
export function Grid({ head, children }: { head: readonly string[]; children: ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-[length:var(--text-sm)]">
        <thead>
          <tr className="border-b border-[var(--color-rule)] text-left">
            {head.map((heading) => (
              <th
                key={heading}
                scope="col"
                className="px-2 py-1.5 text-[length:var(--text-2xs)] font-medium tracking-wide text-[var(--color-ink-muted)] uppercase"
              >
                {heading}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  )
}

/**
 * A confirmation before something irreversible, on the native `<dialog>`.
 *
 * Native rather than a library: it is modal, focus-trapping, and Escape-closing
 * without any of that being reimplemented.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  open: boolean
  title: string
  body: ReactNode
  confirmLabel: string
  onConfirm: () => void
  onCancel: () => void
}) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = ref.current
    if (dialog === null) return
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  return (
    <dialog
      ref={ref}
      onCancel={(event) => {
        event.preventDefault()
        onCancel()
      }}
      aria-label={title}
      className="m-auto max-w-md rounded-[var(--radius-lg)] border border-[var(--color-rule)] bg-[var(--color-surface-raised)] p-0 text-[var(--color-ink)] backdrop:bg-black/40"
    >
      <div className="p-4">
        <h2 className="text-[length:var(--text-base)] font-semibold">{title}</h2>
        <div className="mt-2 text-[length:var(--text-sm)] text-[var(--color-ink-muted)]">
          {body}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button onClick={onCancel}>Cancel</Button>
          <Button variant="danger" onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </dialog>
  )
}
